/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* Personeel.ts
* 8-3-2021 - Jelmer Jellema - Spin in het Web B.V.
* Vast personeel
*/

import {Basemodel, ModelField, ModelRelation} from './basemodel';
import {PersoneelFunctie} from './personeel_functie';
import {Functie} from './functie';
import {PersoneelPlanitem} from './personeel_planitem';
import {PersoneelAfwezigheid} from './personeel_afwezigheid';
import {PersoneelLabel} from './personeel_label';
import {flatten, sortBy, sortedUniq} from 'lodash-es';
import {DateTime} from 'luxon';

export type Personeelgroep = 'Personeel' | 'ZZP Pool' | 'ZZP' | 'Extern';
export class Personeel extends Basemodel {
  static table = 'personeelvast';
  static defaultOrder = ['voornaam', 'tussenvoegsels', 'achternaam'];
  static readonly PersoneelsGroepen = ['Personeel', 'ZZP Pool', 'ZZP', 'Extern'] as const;

  @ModelField() override id: number = null;
  @ModelField() achternaam: string;
  @ModelField() voornaam: string;
  @ModelField() voorletters: string; //ongebruikt
  @ModelField() tussenvoegsels: string;
  @ModelField() woonplaats: string = '';
  @ModelField() telefoonnummer: string = '';
  @ModelField() werkmaatschappij_id: number;
  @ModelField() leverancier_id: number;
  @ModelField() nummer: string;
  @ModelField() amulnummer: string;
  @ModelField() actief: boolean = true;
  @ModelField() email: string;
  @ModelField() groep: Personeelgroep = 'Personeel';
  @ModelField() inlog_actief: boolean = true;
  @ModelField() opmerkingen: string;
  @ModelField() einddatum_certificering: string = null; //yyyy-mm-dd, laatste geldige dag; null = geen einddatum

  @ModelRelation({model: PersoneelFunctie}) personeelvast_functie: PersoneelFunctie[];

  //plans
  @ModelRelation({model: PersoneelPlanitem}) personeel_planitem: PersoneelPlanitem[];

  //afwezigheid
  @ModelRelation({model: PersoneelAfwezigheid}) personeel_afwezigheid: PersoneelAfwezigheid[];

  //labels
  @ModelRelation({model: PersoneelLabel}) personeel_label: PersoneelLabel[];

  /******************************* STATICS *************************************/
  /**
   * Levert alle labels in de huidige divisie op, als dat is toegestaan
   */
  static async alleLabels(): Promise<string[]> {
    //we doen dit via personeel ivm acl
    if (!this.api.can('personeelvast', 'read')) {
      return [];
    }
    //we halen het op via personeel en flatten, wordt toch gecached
    return sortedUniq(sortBy(flatten((await this.getAll([PersoneelLabel], [[Personeel, {
      attributes: ['id'],
      order: []
    }]])).map(p => p.personeel_label.map(l => l.label)))));
  }

  /*********************************** GETTERS ******************************************/
  /**
   * Geef de samengestelde naam volgens het vaste sjabloon dat we hanteren
   */
  get naam(): string {
    //dit is voornaam, tussenvoegsels, achternaam
    let samen: string[] = [];
    for (let deel of [this.voornaam, this.tussenvoegsels, this.achternaam]) {
      let str = deel?.trim();
      if (str?.length) {
        samen.push(str);
      }
    }
    return samen.join(' ');
  }

  /*************************************** Aliassen *************************************/
  get personeel_functie(): PersoneelFunctie[] {
    return this.personeelvast_functie;
  }


  /********************************** info ****************************************/
  /**
   * Geeft true als dit personeelslid deze functie mag uitvoeren. Dan moet dus de sub personeelvast_functie er wel zijn
   * @param functie
   * @param alleenHoofd als true dan wordt alleen op hoofdfunctie gezocht
   */
  public heeftFunctie(functie: Functie | Number, alleenHoofd: boolean = false): boolean {
    let id: Number = (functie instanceof Functie) ? functie.id : functie;
    if (this.personeel_functie) {
      return alleenHoofd ?
        this.personeel_functie.some(pf => pf.functie_id === id && pf.hoofdfunctie)
        :
        this.personeel_functie.some(pf => pf.functie_id === id);
    } else {
      return false;
    }
  }

  /**
   * Geeft true als de algemene certificering (raamovereenkomst) van dit personeelslid op de opgegeven datum nog geldig
   * is. Zonder einddatum is dat altijd zo; met einddatum geldt de certificering tot en met die dag.
   * Zonder datum kijken we naar vandaag.
   * @param datum yyyy-mm-dd
   */
  public certificeringGeldigOp(datum: string = DateTime.now().toISODate()): boolean {
    return !this.einddatum_certificering || !datum || datum <= this.einddatum_certificering;
  }

  /**
   * De functies van dit personeelslid waarvan de certificering op de opgegeven datum verlopen is.
   * Zonder datum kijken we naar vandaag.
   * @param datum yyyy-mm-dd
   */
  public verlopenFuncties(datum: string = DateTime.now().toISODate()): PersoneelFunctie[] {
    return this.personeel_functie?.filter(pf => !pf.geldigOp(datum)) || [];
  }

  /**
   * Geeft true als op de opgegeven datum de algemene certificering (raamovereenkomst) verlopen is, of die van een van
   * de functies van dit personeelslid. Zonder datum kijken we naar vandaag.
   * @param datum yyyy-mm-dd
   */
  public certificeringVerlopen(datum: string = DateTime.now().toISODate()): boolean {
    return (!this.certificeringGeldigOp(datum)) || (this.verlopenFuncties(datum).length > 0);
  }

  /**
   * De meldingen over verlopen certificeringen: een regel voor de algemene certificering (in ui: raamovereenkomst) en een per
   * functie waarvan de certificering verlopen is. Een lege array betekent dat alles nog geldig is.
   * Zonder datum kijken we naar vandaag.
   * @param datum yyyy-mm-dd
   */
  public certificeringmeldingen(datum: string = DateTime.now().toISODate()): string[] {
    const meldingen: string[] = [];
    if (!this.certificeringGeldigOp(datum)) {
      meldingen.push(`De raamovereenkomst is verlopen op ${this.nlDatum(this.einddatum_certificering)}.`);
    }
    for (const pf of this.verlopenFuncties(datum)) {
      meldingen.push(`De certificering voor ${pf.functie?.naam || 'een van de functies'} is verlopen op ${this.nlDatum(pf.einddatum_certificering)}.`);
    }
    return meldingen;
  }

  /**
   * Levert een sql-datum in de Nederlandse notatie dd-mm-jjjj, of '?' als hij niet te lezen is.
   * @param datum
   */
  private nlDatum(datum: string): string {
    const dt = DateTime.fromSQL(datum || '');
    return dt.isValid ? dt.toFormat('dd-MM-yyyy') : '?';
  }

  /**
   * Geeft true als dit personeelslid deze functie mag uitvoeren én de certificering voor die functie
   * op de opgegeven datum nog geldig is. De algemene certificering telt hier niet mee, die staat los.
   * @param functie
   * @param datum yyyy-mm-dd
   * @param alleenHoofd als true dan wordt alleen op hoofdfunctie gezocht
   */
  public heeftFunctieGeldigOp(functie: Functie | Number, datum: string, alleenHoofd: boolean = false): boolean {
    let id: Number = (functie instanceof Functie) ? functie.id : functie;
    return !!this.personeel_functie?.some(pf => pf.functie_id === id && (alleenHoofd ? pf.hoofdfunctie : true) && pf.geldigOp(datum));
  }

  /**
   * Helper: controleert de (al ingeladen!) afwezigheid om te bepalen of het personeelslid op een gegeven moment afwezig is. Zie personeel_afwezigheid.afwezigOp
   * @param datestr
   * @param begintijd
   * @param eindtijd
   */
  public afwezigOp(datestr: string, begintijd?: string, eindtijd?: string): boolean {
    return (this.personeel_afwezigheid && this.personeel_afwezigheid.some(pa => pa.afwezigOp(datestr, begintijd, eindtijd)));
  }

  /**
   * Helper: heeft dit personeelslid een label met de opgegeven naam?
   * @param label
   */
  heeftLabel(label: string) {
    return !!this.personeel_label?.some(pl => pl.label.toLowerCase() === label.toLowerCase());
  }
}
